import axios from 'axios';

const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

const client = axios.create({
  baseURL,
  timeout: 30000  // 30s statt 120s — schnelleres Feedback bei Hangs
});

// Request-Interceptor: Auth Token aus localStorage anhaengen
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  if (import.meta.env.DEV) {
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
  }

  return config;
});

// ── Auto-Refresh statt sofortigem Logout ──────────────────────────────────────
// Der accessToken gilt nur 15 Min. Frueher wurde der User bei jedem Ablauf hart
// auf /login geworfen (mitten in der Arbeit). Jetzt: bei 401 einmal per refreshToken
// transparent einen neuen accessToken holen und den Original-Request wiederholen.
// Erst wenn AUCH der Refresh scheitert (refreshToken ungueltig) → echter Logout.
let refreshPromise = null; // single-flight: nur EIN Refresh, auch bei vielen parallelen 401ern

function doLogout() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('kein refreshToken');
  // Bare axios (NICHT `client`) → laeuft nicht durch diesen Interceptor (keine Rekursion).
  const { data } = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
  if (!data?.accessToken) throw new Error('kein accessToken in Refresh-Antwort');
  localStorage.setItem('accessToken', data.accessToken);
  if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
  return data.accessToken;
}

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Optimistic Locking: 409 als User-sichtbare Warnung
    if (error.response?.status === 409) {
      const msg = error.response?.data?.message || 'Inhalt wurde zwischenzeitlich geaendert. Bitte neu laden.';
      if (typeof window !== 'undefined') window.alert(msg);
      return Promise.reject(error);
    }

    const original = error.config;
    const status = error.response?.status;
    const isRefreshCall = !!original?.url && original.url.includes('/auth/refresh');

    // 401 = accessToken abgelaufen → einmal erneuern + Request wiederholen
    if (status === 401 && original && !original._retry && !isRefreshCall && localStorage.getItem('refreshToken')) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
        }
        await refreshPromise;              // alle parallelen 401er warten auf denselben Refresh
        return client(original);           // Request-Interceptor haengt den neuen Token an
      } catch (refreshErr) {
        doLogout();
        return Promise.reject(error);
      }
    }

    // 401 ohne Refresh-Moeglichkeit (oder Refresh gescheitert) → ausloggen
    if (status === 401) {
      doLogout();
    }

    return Promise.reject(error);
  }
);

export default client;
